<template>
  <MainLayout>
    <div v-if="hasLoaded" class="space-y-3">
      <DashboardQuickstartCard />

      <div
        v-if="loadError"
        class="flex flex-col gap-3 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700 md:flex-row md:items-center md:justify-between"
      >
        <span>{{ loadError }}</span>
        <Button size="sm" variant="outline" @click="fetchDashboard">Retry</Button>
      </div>

      <div class="grid grid-cols-1 gap-4 xl:grid-cols-2 xl:items-stretch">
        <DashboardMeetingsCard
          :meetings="meetings"
          :selected-date="selectedMeetingDate"
          @update:selected-date="updateMeetingDate"
        />

        <DashboardTasksCard :tasks="tasks" :total-incomplete="totalIncompleteTasks" />
      </div>

      <DashboardUnreadMessagesCard :messages="unreadMessages" :total-unread-threads="totalUnreadThreads" />

      <div class="mt-4 grid grid-cols-1 gap-4 xl:grid-cols-2 xl:items-start">
        <DashboardRecruitingFunnelCard
          :funnel="recruitingFunnel"
          :metrics-timeline="recruitingMetricsTimeline"
          :custom-timeframe-label="recruitingCustomTimeframeLabel"
          :custom-start="recruitingCustomReportingTimeframe.start"
          :custom-end="recruitingCustomReportingTimeframe.end"
          @set-timeline="setRecruitingTimeline"
          @update:custom-start="recruitingCustomReportingTimeframe.start = $event"
          @update:custom-end="recruitingCustomReportingTimeframe.end = $event"
          @apply-custom-timeframe="applyRecruitingCustomTimeframe"
        />
        <DashboardPipelineReviewsCard
          :pipeline-reviews="pipelineReviews"
          :metrics-timeline="pipelineMetricsTimeline"
          :custom-timeframe-label="pipelineCustomTimeframeLabel"
          :custom-start="pipelineCustomReportingTimeframe.start"
          :custom-end="pipelineCustomReportingTimeframe.end"
          :pipeline-reviewer-filter="pipelineReviewerFilter"
          :eligible-pipeline-reviewers="eligiblePipelineReviewers"
          @set-timeline="setPipelineTimeline"
          @update:custom-start="pipelineCustomReportingTimeframe.start = $event"
          @update:custom-end="pipelineCustomReportingTimeframe.end = $event"
          @apply-custom-timeframe="applyPipelineCustomTimeframe"
          @update:pipeline-reviewer-filter="updatePipelineReviewer"
        />
      </div>
    </div>
  </MainLayout>
</template>

<script setup lang="ts">
import { computed, onMounted, reactive, ref } from 'vue';
import { format } from 'date-fns';
import type { DashboardMetricsTimeline, DashboardResponse } from '@/api/dashboard';
import { Api } from '@/api';
import DashboardMeetingsCard from '@/components/dashboard/DashboardMeetingsCard.vue';
import DashboardPipelineReviewsCard from '@/components/dashboard/DashboardPipelineReviewsCard.vue';
import DashboardQuickstartCard from '@/components/dashboard/DashboardQuickstartCard.vue';
import DashboardRecruitingFunnelCard from '@/components/dashboard/DashboardRecruitingFunnelCard.vue';
import DashboardTasksCard from '@/components/dashboard/DashboardTasksCard.vue';
import DashboardUnreadMessagesCard from '@/components/dashboard/DashboardUnreadMessagesCard.vue';
import MainLayout from '@/components/layouts/MainLayout/index.vue';
import { Button } from '@/components/ui/button';
import { useLoadingSpinnerStore } from '@/stores/loadingSpinner';

const loadingSpinnerStore = useLoadingSpinnerStore();

const selectedMeetingDate = ref(format(new Date(), 'yyyy-MM-dd'));
const recruitingMetricsTimeline = ref<DashboardMetricsTimeline>('This Month');
const pipelineMetricsTimeline = ref<DashboardMetricsTimeline>('This Month');
const pipelineReviewerFilter = ref('All');
const loadError = ref<string | null>(null);
const hasLoaded = ref(false);
const dashboardData = ref<DashboardResponse | null>(null);

const recruitingCustomReportingTimeframe = reactive<{
  start: string | null;
  end: string | null;
}>({
  start: null,
  end: null,
});

const pipelineCustomReportingTimeframe = reactive<{
  start: string | null;
  end: string | null;
}>({
  start: null,
  end: null,
});

const meetings = computed(() => dashboardData.value?.meetings.records ?? []);
const tasks = computed(() => dashboardData.value?.user_tasks.records ?? []);
const totalIncompleteTasks = computed(() => dashboardData.value?.user_tasks.total_incomplete ?? 0);
const unreadMessages = computed(() => dashboardData.value?.unread_messages.records ?? []);
const totalUnreadThreads = computed(() => dashboardData.value?.unread_messages.total_unread_threads ?? 0);
const recruitingFunnel = computed(
  () =>
    dashboardData.value?.recruiting_funnel ?? {
      sourced: 0,
      phone_screened: 0,
      interviewed: 0,
      hired: 0,
      phone_screen_rate: 0,
      interview_rate: 0,
      hire_rate: 0,
    }
);
const pipelineReviews = computed(
  () =>
    dashboardData.value?.pipeline_reviews ?? {
      completed: 0,
      completion_rate: 0,
      score_distribution: { '1': 0, '2': 0, '3': 0, '4': 0, '5': 0 },
      unscored: 0,
    }
);
const eligiblePipelineReviewers = computed(() => dashboardData.value?.eligible_pipeline_reviewers ?? []);

const formatCustomTimeframeLabel = (start: string | null, end: string | null) => {
  if (start && end) {
    return `${start} - ${end}`;
  }

  return 'Custom';
};

const recruitingCustomTimeframeLabel = computed(() =>
  formatCustomTimeframeLabel(recruitingCustomReportingTimeframe.start, recruitingCustomReportingTimeframe.end)
);
const pipelineCustomTimeframeLabel = computed(() =>
  formatCustomTimeframeLabel(pipelineCustomReportingTimeframe.start, pipelineCustomReportingTimeframe.end)
);

const fetchDashboard = async () => {
  loadError.value = null;
  loadingSpinnerStore.showLoadingSpinner();

  try {
    const response = await Api.Dashboard.getDashboard({
      meetingDate: selectedMeetingDate.value,
      recruitingMetricsTimeline: recruitingMetricsTimeline.value,
      pipelineMetricsTimeline: pipelineMetricsTimeline.value,
      pipelineReviewer: pipelineReviewerFilter.value,
      recruitingCustomStart: recruitingCustomReportingTimeframe.start,
      recruitingCustomEnd: recruitingCustomReportingTimeframe.end,
      pipelineCustomStart: pipelineCustomReportingTimeframe.start,
      pipelineCustomEnd: pipelineCustomReportingTimeframe.end,
    });

    dashboardData.value = response;
    selectedMeetingDate.value = response.meetings.selected_date;
  } catch (error) {
    console.error('Failed to load dashboard', error);
    loadError.value = 'Dashboard data failed to load. Please try again.';
  } finally {
    loadingSpinnerStore.hideLoadingSpinner();
    hasLoaded.value = true;
  }
};

const updateMeetingDate = async (value: string) => {
  selectedMeetingDate.value = value;
  await fetchDashboard();
};

const setRecruitingTimeline = async (timeline: DashboardMetricsTimeline) => {
  recruitingMetricsTimeline.value = timeline;
  await fetchDashboard();
};

const setPipelineTimeline = async (timeline: DashboardMetricsTimeline) => {
  pipelineMetricsTimeline.value = timeline;
  await fetchDashboard();
};

const applyRecruitingCustomTimeframe = async () => {
  if (!recruitingCustomReportingTimeframe.start || !recruitingCustomReportingTimeframe.end) return;

  recruitingMetricsTimeline.value = 'Custom';
  await fetchDashboard();
};

const applyPipelineCustomTimeframe = async () => {
  if (!pipelineCustomReportingTimeframe.start || !pipelineCustomReportingTimeframe.end) return;

  pipelineMetricsTimeline.value = 'Custom';
  await fetchDashboard();
};

const updatePipelineReviewer = async (value: string) => {
  pipelineReviewerFilter.value = value;
  await fetchDashboard();
};

onMounted(async () => {
  await fetchDashboard();
});
</script>
